import { useState } from 'react';
import { privateApi } from '../core/http';
import { compressImage } from '../core/utils/imageCompressor';
export const useMinioUpload = (props = { bucketType: 'PUBLIC', autoCompress: true }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const uploadFile = async (rawFile) => {
        setIsUploading(true);
        setProgress(0);
        setError(null);
        try {
            // 0. Kompresi gambar otomatis di sisi client (browser) sebelum request jaringan
            let fileToUpload = rawFile;
            const shouldCompress = props.autoCompress !== false && rawFile.type.startsWith('image/');
            if (shouldCompress) {
                try {
                    fileToUpload = await compressImage(rawFile, props.compressionOptions);
                }
                catch (compressErr) {
                    console.warn('[useMinioUpload] Compression fallback to raw file:', compressErr);
                    fileToUpload = rawFile;
                }
            }
            // 1. Request Presigned URL dari backend pena-storage via gateway (dengan kredensial JWT terotentikasi)
            const requestPayload = {
                originalName: fileToUpload.name,
                sizeBytes: fileToUpload.size,
                mimeType: fileToUpload.type || 'image/png',
                bucketType: props.bucketType || 'PUBLIC',
            };
            const res = await privateApi().post('/v1/storage/upload-request', requestPayload);
            const presignedData = res.data?.data;
            if (!presignedData || !presignedData.presignedUrl) {
                throw new Error('Gagal mendapatkan URL upload dari storage service');
            }
            const { presignedUrl, fileId, objectPath } = presignedData;
            // 2. Upload langsung (direct-to-S3/MinIO) via PUT dengan XMLHttpRequest untuk melacak realtime progress
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = Math.round((event.loaded / event.total) * 100);
                        setProgress(percentComplete);
                    }
                };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    }
                    else {
                        reject(new Error(`Gagal mengunggah file ke MinIO storage (HTTP ${xhr.status})`));
                    }
                };
                xhr.onerror = () => reject(new Error('Koneksi jaringan terputus saat upload file'));
                xhr.open('PUT', presignedUrl, true);
                xhr.setRequestHeader('Content-Type', fileToUpload.type || 'image/png');
                xhr.send(fileToUpload);
            });
            // 3. Konfirmasi upload ke backend storage agar status tercatat di pena_storage.file_metadata
            await privateApi().post('/v1/storage/confirm', { fileId });
            setIsUploading(false);
            setProgress(100);
            // URL permanen publik (tanpa query query signature AWS yang expired dalam 5 menit)
            const publicUrl = presignedUrl.split('?')[0];
            return { fileId, objectPath, presignedUrl, publicUrl };
        }
        catch (err) {
            const errMsg = err?.response?.data?.message || err?.message || 'Terjadi kesalahan saat mengunggah file';
            setError(errMsg);
            setIsUploading(false);
            setProgress(0);
            return null;
        }
    };
    const deleteUploadedFile = async (fileId) => {
        try {
            await privateApi().delete(`/v1/storage/${fileId}`);
            return true;
        }
        catch (e) {
            console.error('Failed to delete cancelled file from storage:', e);
            return false;
        }
    };
    return {
        uploadFile,
        deleteUploadedFile,
        isUploading,
        progress,
        error,
    };
};
